import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleNotch, FloppyDisk } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import { PayoutMethodField } from "@/components/enrollment/PayoutMethodField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { billingChanges } from "@/components/onboarding/onboarding-logic";
import { SectionShell, SettingRow } from "./section-primitives";
import type { ProfileWithMeta } from "./types";
import type { PayoutMethod } from "@/types";

export function BillingSection({ profile }: { profile: ProfileWithMeta }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>(profile.payoutMethod);
  const [billing, setBilling] = useState<BillingIdentityValue>({
    businessName: profile.businessName ?? "",
    businessAddress: profile.businessAddress ?? "",
    businessTaxId: profile.businessTaxId ?? "",
    businessVatId: profile.businessVatId ?? "",
    personalAddress: profile.personalAddress ?? "",
    personalTaxId: profile.personalTaxId ?? "",
    bankName: profile.bankName ?? "",
    bankAccount: profile.bankAccount ?? "",
  });

  // Stanje kakvo je u bazi; šalju se samo polja koja su se promijenila od njega.
  const saved = useRef<{ payoutMethod: PayoutMethod; billing: BillingIdentityValue }>({ payoutMethod, billing });

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiSend(`/api/therapists/${profile.id}`, "PATCH", body, "Greška pri spremanju naplate"),
    onSuccess: () => {
      saved.current = { payoutMethod, billing };
      queryClient.invalidateQueries({ queryKey: ["therapist", "profile", "me"] });
      toast.success("Podaci za naplatu su sačuvani.");
    },
  });

  const set = (patch: Partial<BillingIdentityValue>) => setBilling((prev) => ({ ...prev, ...patch }));

  const save = () => {
    // Isto pravilo kao onboarding i prijava (validateBilling), ali samo za izmijenjena polja.
    const { payload, error } = billingChanges(payoutMethod, billing, saved.current.billing);
    if (error) {
      toast.error(error);
      return;
    }
    // Način isplate se šalje uvijek kad se promijeni, i kad su polja nove grane prazna.
    const body = {
      ...(payoutMethod !== saved.current.payoutMethod ? { payoutMethod } : {}),
      ...payload,
    };
    if (Object.keys(body).length === 0) {
      toast.success("Nema izmjena za spremanje.");
      return;
    }
    update.mutate(body);
  };

  return (
    <SectionShell
      title="Podaci za račune i isplate"
      description="Zarada ostaje zaseban pregled novca; ovdje se čuvaju identifikacijski podaci za administraciju."
      footer={
        profile.isVolunteer ? undefined : (
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
            Sačuvaj naplatu
          </Button>
        )
      }
    >
      {profile.isVolunteer ? (
        <p className="rounded-lg border border-border/60 bg-muted px-3.5 py-3 text-sm leading-relaxed text-muted-foreground">
          {t("billingIdentity.volunteerHint")}
        </p>
      ) : (
        <div className="space-y-4">
          <PayoutMethodField value={payoutMethod} onChange={setPayoutMethod} disabled={update.isPending} />
          {payoutMethod === "invoice" ? (
        <div>
          <SettingRow label={t("billingIdentity.businessName")} htmlFor="billing-business-name">
            <Input
              id="billing-business-name"
              value={billing.businessName ?? ""}
              onChange={(e) => set({ businessName: e.target.value })}
            />
          </SettingRow>
          <SettingRow label={t("billingIdentity.businessAddress")} htmlFor="billing-business-address">
            <Input
              id="billing-business-address"
              value={billing.businessAddress ?? ""}
              onChange={(e) => set({ businessAddress: e.target.value })}
            />
          </SettingRow>
          <SettingRow label={t("billingIdentity.businessTaxId")} htmlFor="billing-business-tax">
            <Input
              id="billing-business-tax"
              inputMode="numeric"
              maxLength={13}
              value={billing.businessTaxId ?? ""}
              onChange={(e) => set({ businessTaxId: e.target.value })}
            />
          </SettingRow>
          <SettingRow label={t("billingIdentity.businessVatId")} htmlFor="billing-business-vat">
            <Input
              id="billing-business-vat"
              value={billing.businessVatId ?? ""}
              onChange={(e) => set({ businessVatId: e.target.value })}
            />
          </SettingRow>
        </div>
      ) : (
        <div>
          <SettingRow label={t("billingIdentity.personalAddress")} htmlFor="billing-personal-address">
            <Input
              id="billing-personal-address"
              value={billing.personalAddress ?? ""}
              onChange={(e) => set({ personalAddress: e.target.value })}
            />
          </SettingRow>
          <SettingRow label={t("billingIdentity.personalTaxId")} htmlFor="billing-personal-tax">
            <Input
              id="billing-personal-tax"
              inputMode="numeric"
              maxLength={13}
              value={billing.personalTaxId ?? ""}
              onChange={(e) => set({ personalTaxId: e.target.value })}
            />
          </SettingRow>
        </div>
          )}
          <div>
            <SettingRow label={`${t("billingIdentity.bankName")} *`} htmlFor="billing-bank-name">
              <Input
                id="billing-bank-name"
                maxLength={120}
                value={billing.bankName ?? ""}
                onChange={(e) => set({ bankName: e.target.value })}
              />
            </SettingRow>
            <SettingRow
              label={`${t("billingIdentity.bankAccount")} *`}
              description={t("billingIdentity.bankAccountHint")}
              htmlFor="billing-bank-account"
            >
              <Input
                id="billing-bank-account"
                maxLength={42}
                value={billing.bankAccount ?? ""}
                onChange={(e) => set({ bankAccount: e.target.value })}
              />
            </SettingRow>
          </div>
        </div>
      )}
    </SectionShell>
  );
}
